import interpolation from './interpolation'

export function fromPolarX (angle, length = 1, x = 0) {
  return x + Math.cos(angle) * length
}

export function fromPolarY (angle, length = 1, y = 0) {
  return y + Math.sin(angle) * length
}

export function fromPolar (angle, length = 1, x = 0, y = 0) {
  return create(
    fromPolarX(angle, length, x),
    fromPolarY(angle, length, y)
  )
}

export function create (x = 0, y = 0) {
  return [x, y]
}

export function setX (out, x) {
  out[0] = x
  return out
}

export function setY (out, y) {
  out[1] = y
  return out
}

export function set (out, x, y) {
  return setY(setX(out, x), y)
}

export function reset (out) {
  return set(out, 0, 0)
}

export function x (out, x) {
  if (x) {
    setX(out, x)
  }
  return out[0]
}

export function y (out, y) {
  if (y) {
    setY(out, y)
  }
  return out[1]
}

export function polarX (out, angle, length = 1) {
  return setX(out, Math.cos(angle) * length)
}

export function polarY (out, angle, length = 1) {
  return setY(out, Math.sin(angle) * length)
}

export function polar (out, angle, length = 1) {
  return polarY(polarX(out, angle, length), angle, length)
}

export function copy (out, [x, y]) {
  return set(out, x, y)
}

export function clone ([x, y]) {
  return create(x, y)
}

export function lengthSquaredOf (x, y) {
  return x * x + y * y
}

export function lengthSquared ([x, y]) {
  return lengthSquaredOf(x, y)
}

export function lengthOf (x, y) {
  return Math.hypot(x, y)
}

export function length ([x, y]) {
  return lengthOf(x, y)
}

export function directionOf (x, y) {
  return Math.atan2(y, x)
}

export function direction ([x, y]) {
  return directionOf(x, y)
}

export function directionBetween ([ax, ay], [bx, by]) {
  return Math.atan2(ay - by, ax - bx)
}

export function distanceBetween ([ax, ay], [bx, by]) {
  return Math.hypot(ax - bx, ay - by)
}

export function add (out, [ax, ay], [bx, by]) {
  return set(out, ax + bx, ay + by)
}

export function substract (out, [ax, ay], [bx, by]) {
  return set(out, ax - bx, ay - by)
}

export function multiply (out, [ax, ay], [bx, by]) {
  return set(out, ax * bx, ay * by)
}

export function scale (out, [x, y], s) {
  return set(out, x * s, y * s)
}

export function divide (out, [ax, ay], [bx, by]) {
  return set(out, ax / bx, ay / by)
}

export function invert (out, [x, y]) {
  return set(out, -x, -y)
}

export function normalize (out, [x, y]) {
  const l = lengthOf(x, y)
  return set(out, x / l, y / l)
}

export function linear (out, x, [ax, ay], [bx, by]) {
  return set(out,
    interpolation.linear(x, ax, bx),
    interpolation.linear(x, ay, by)
  )
}

export function quadratic (out, x, [ax, ay], [bx, by], [cx, cy]) {
  return set(out,
    interpolation.quadratic(x, ax, bx, cx),
    interpolation.quadratic(x, ay, by, cy)
  )
}

export function cubic (out, x, [ax, ay], [bx, by], [cx, cy], [dx, dy]) {
  return set(out,
    interpolation.cubic(x, ax, bx, cx, dx),
    interpolation.cubic(x, ay, by, cy, dy)
  )
}

export function dot ([ax, ay], [bx, by]) {
  return ax * bx + ay * by
}

export function cross ([ax, ay], [bx, by]) {
  return ax * by - ay * bx
}

export function perpLeft (out, [x, y]) {
  return set(out, y, -x)
}

export function perpRight (out, [x, y]) {
  return set(out, -y, x)
}

export function stringify ([x, y]) {
  return `vec2(${x}, ${y})`
}

export function approximate (out, [cx, cy], [tx, ty], delta = 0.01) {
  return set(out, cx + (tx - cx) * delta, cy + (ty - cy) * delta)
}

export default {
  fromPolar,
  fromPolarX,
  fromPolarY,
  create,
  setX,
  setY,
  set,
  reset,
  x,
  y,
  polarX,
  polarY,
  polar,
  copy,
  clone,
  lengthOf,
  length,
  directionOf,
  direction,
  directionBetween,
  distanceBetween,
  add,
  substract,
  multiply,
  scale,
  divide,
  invert,
  normalize,
  linear,
  quadratic,
  cubic,
  perpLeft,
  perpRight,
  stringify,
  approximate
}
